import React, { useState, useEffect, useRef, useMemo } from 'react';
import { updateDoc, doc, serverTimestamp, query, collection, where, getDocs, limit } from 'firebase/firestore';

import ImageUpload from '../../components/ui/ImageUpload';
import PlannerIcons from '../../components/shared/PlannerIcons';
import IFramePreview from '../../components/ui/IFramePreview';
import PlannerWebsiteRenderer from './PlannerWebsiteRenderer';
import { useToast } from '../../contexts/ToastContext';

export default function PlannerWebsiteEditor({ user, db, storage, navigateTo }) {
    const toast = useToast(); 
    const defaultTheme = { primaryColor: '#db2777', backgroundColor: '#ffffff', fontFamily: "'Playfair Display', serif", borderRadius: '0.5rem' };
    const defaultSections = [
        { 
            id: 'hero', type: 'hero', enabled: true, 
            content: { title: user.plannerName || "Elite Event Planning", subtitle: "Designing unforgettable experiences.", imageUrl: "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg", buttonText: "View Packages", overlayOpacity: 0.5 } 
        },
        { 
            id: 'about', type: 'about', enabled: true, 
            content: { heading: "Meet Your Planner", text: "With over 5 years of experience...", imageUrl: user.profilePicUrl || "", layout: "left" } 
        },
        { 
            id: 'services', type: 'services', enabled: true, 
            content: { 
                heading: "Investment & Services", subtitle: "Packages tailored to your needs.",
                packages: [
                    { title: "Full Service", price: "5000", description: "Comprehensive planning from start to finish.", features: ["Budget Management", "Vendor Curation", "Design & Styling", "Day-Of Coordination"] },
                    { title: "Month-Of", price: "2500", description: "Take over in the final weeks to ensure smooth sailing.", features: ["Timeline Creation", "Vendor Takeover", "Rehearsal Management", "Day-Of Coordination"] }
                ]
            } 
        },
        {
            id: 'gallery', type: 'gallery', enabled: true,
            content: { heading: "Portfolio", images: [{url: "https://images.pexels.com/photos/256737/pexels-photo-256737.jpeg", caption: "Rustic Wedding"}, {url: "https://images.pexels.com/photos/265947/pexels-photo-265947.jpeg", caption: "Corporate Gala"}] }
        },
        {
            id: 'contact', type: 'contact', enabled: true,
            content: { heading: "Ready to get started?", text: "We would love to hear about your vision. Contact us below to schedule a free consultation." }
        }
    ];

    const [sections, setSections] = useState(user.plannerWebsite?.sections || defaultSections);
    const [theme, setTheme] = useState({ ...defaultTheme, ...(user.plannerWebsite?.theme || {}) });
    const [activeSectionId, setActiveSectionId] = useState(sections[0].id);
    const [saving, setSaving] = useState(false);
    const [isAutoSaving, setIsAutoSaving] = useState(false); // NEW: Auto-save state
    const [copiedLink, setCopiedLink] = useState(false);
    const [isPublished, setIsPublished] = useState(user.plannerWebsite?.isPublished || false);
    
    // NEW: Subdomain state
    const [subdomain, setSubdomain] = useState(user.plannerWebsite?.subdomain || '');

    // NEW: Modern Editor States
    const [previewDevice, setPreviewDevice] = useState('desktop'); 
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [editorTab, setEditorTab] = useState('content'); 
    const [templateToApply, setTemplateToApply] = useState(null); 

    // History States
    const [past, setPast] = useState([]);
    const [future, setFuture] = useState([]);

    const autoSaveTimeoutRef = useRef(null);
    const initialLoadRef = useRef(true);

    const publicUrl = `${window.location.origin}/#planner/${subdomain || user.uid}`;

    // NEW: Auto-Save Effect for Planner Pro
    useEffect(() => {
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        
        autoSaveTimeoutRef.current = setTimeout(async () => {
            setIsAutoSaving(true);
            try {
                // Auto-save specifically targets nested properties to avoid overwriting other user data
                await updateDoc(doc(db, "users", user.uid), {
                    "plannerWebsite.sections": sections,
                    "plannerWebsite.theme": theme,
                    "plannerWebsite.subdomain": subdomain,
                    "plannerWebsite.updatedAt": serverTimestamp()
                });
            } catch (err) {
                console.error("Auto-save error:", err);
            } finally {
                setIsAutoSaving(false);
            }
        }, 3000); // 3 seconds after last change
        
        return () => clearTimeout(autoSaveTimeoutRef.current);
    }, [sections, theme, subdomain, db, user.uid]);

    // NEW: Full Website Templates
    const portfolioTemplates = [
        {
            id: 'classic',
            name: 'Classic Elegance',
            theme: { primaryColor: '#d8a7b1', backgroundColor: '#fdfbfb', fontFamily: "'Playfair Display', serif", borderRadius: '0px' },
            sections: [
                { id: 'hero_1', type: 'hero', enabled: true, content: { title: user.plannerName || "Classic Weddings", subtitle: "Timeless events designed with elegance and grace.", imageUrl: "https://images.pexels.com/photos/256737/pexels-photo-256737.jpeg", buttonText: "Inquire Now", buttonAction: 'scroll', alignment: 'center', overlayOpacity: 0.3, height: '600px' } },
                { id: 'about_1', type: 'about', enabled: true, content: { heading: "About Me", text: "Hi, I'm so glad you're here!\n\nI specialize in crafting beautiful memories for couples who want a seamless, stress-free wedding day. With an eye for detail and a passion for romance, my team and I handle the logistics so you can focus on the love.", imageUrl: user.profilePicUrl || "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg", layout: "left" } },
                { id: 'services_1', type: 'services', enabled: true, content: { heading: "Investment", subtitle: "Tailored to your perfect day.", packages: [{ title: "Full Service Planning", price: "5000", description: "From the first mood board to the final dance.", features: ["Budget Management", "Vendor Curation", "Event Design", "Day-Of Coordination"] }, { title: "Month-Of Coordination", price: "2500", description: "Take over in the final weeks.", features: ["Timeline Creation", "Vendor Takeover", "Rehearsal Management"] }] } },
                { id: 'gallery_1', type: 'gallery', enabled: true, content: { heading: "Portfolio", images: [{url: "https://images.pexels.com/photos/2253870/pexels-photo-2253870.jpeg"}, {url: "https://images.pexels.com/photos/1739502/pexels-photo-1739502.jpeg"}] } },
                { id: 'contact_1', type: 'contact', enabled: true, content: { heading: "Let's Connect", text: "We would love to hear about your vision. Contact us to schedule a free consultation." } }
            ]
        },
        {
            id: 'modern',
            name: 'Modern Minimal',
            theme: { primaryColor: '#111827', backgroundColor: '#ffffff', fontFamily: "'Inter', sans-serif", borderRadius: '0.5rem' },
            sections: [
                { id: 'hero_2', type: 'hero', enabled: true, content: { title: user.plannerName || "Modern Events", subtitle: "Clean. Sleek. Perfect.", imageUrl: "https://images.pexels.com/photos/3171837/pexels-photo-3171837.jpeg", buttonText: "View Work", buttonAction: 'scroll', alignment: 'left', overlayOpacity: 0.5, height: '100vh' } },
                { id: 'about_2', type: 'about', enabled: true, content: { heading: "The Studio", text: "A modern approach to event design.\n\nWe strip away the noise to focus on what matters: impactful design, flawless execution, and unforgettable guest experiences.", imageUrl: user.profilePicUrl || "https://images.pexels.com/photos/265947/pexels-photo-265947.jpeg", layout: "right" } },
                { id: 'services_2', type: 'services', enabled: true, content: { heading: "Capabilities", subtitle: "", packages: [{ title: "Event Production", price: "", description: "Comprehensive corporate and private event execution.", features: ["Logistics", "Sourcing", "On-site Management"] }] } },
                { id: 'contact_2', type: 'contact', enabled: true, content: { heading: "Ready?", text: "Send us a message to start building." } }
            ]
        },
        {
            id: 'boho',
            name: 'Boho Earth',
            theme: { primaryColor: '#bc6c25', backgroundColor: '#f4f1de', fontFamily: "'Georgia', serif", borderRadius: '1rem' },
            sections: [
                { id: 'hero_3', type: 'hero', enabled: true, content: { title: user.plannerName || "Boho Gatherings", subtitle: "Earthy, intimate, beautiful.", imageUrl: "https://images.pexels.com/photos/1739502/pexels-photo-1739502.jpeg", buttonText: "Let's Chat", buttonAction: 'chat', alignment: 'center', overlayOpacity: 0.4, height: '600px' } },
                { id: 'about_3', type: 'about', enabled: true, content: { heading: "My Approach", text: "Rooted in nature.\n\nI love working with couples who want a relaxed, joyful celebration surrounded by natural textures, warm tones, and their closest friends.", imageUrl: user.profilePicUrl || "https://images.pexels.com/photos/1045541/pexels-photo-1045541.jpeg", layout: "left" } },
                { id: 'services_3', type: 'services', enabled: true, content: { heading: "Packages", subtitle: "", packages: [{ title: "Micro-Weddings", price: "3000", description: "Intimate focus for 50 guests or fewer.", features: ["Venue styling", "Vendor coordination", "Timeline management"] }] } },
                { id: 'gallery_3', type: 'gallery', enabled: true, content: { heading: "Recent Magic", images: [{url: "https://images.pexels.com/photos/1045541/pexels-photo-1045541.jpeg"}, {url: "https://images.pexels.com/photos/3171837/pexels-photo-3171837.jpeg"}] } },
                { id: 'contact_3', type: 'contact', enabled: true, content: { heading: "Connect", text: "Say hello and tell me about your day." } }
            ]
        }
    ];

    // NEW: Designer Color Palettes
    const colorPalettes = [
        { name: "Blush Elegance", primary: "#d8a7b1", bg: "#fdfbfb" },
        { name: "Modern Monochrome", primary: "#111827", bg: "#ffffff" },
        { name: "Earthy Boho", primary: "#bc6c25", bg: "#f4f1de" },
        { name: "Champagne & Gold", primary: "#d4af37", bg: "#faf9f6" },
        { name: "Coastal Serenity", primary: "#4a6fa5", bg: "#f0f4f8" },
        { name: "Sage & Linen", primary: "#8a9a86", bg: "#fafaf9" },
        { name: "Dusty Rose", primary: "#C08497", bg: "#F5F5F5" }
    ];

    // Centralized Design Updater for History tracking
    const updateDesign = (newSections, newTheme) => {
        setPast(prev => [...prev, { sections, theme }]);
        setFuture([]);
        setSections(newSections);
        setTheme(newTheme);
    };

    const handleUndo = () => {
        if (past.length === 0) return;
        const previous = past[past.length - 1];
        setFuture(prev => [{ sections, theme }, ...prev]);
        setPast(prev => prev.slice(0, -1));
        setSections(previous.sections);
        setTheme(previous.theme);
    };

    const handleRedo = () => {
        if (future.length === 0) return;
        const next = future[0];
        setPast(prev => [...prev, { sections, theme }]);
        setFuture(prev => prev.slice(1));
        setSections(next.sections);
        setTheme(next.theme);
    };

    const handleSave = async (publish = false) => {
        setSaving(true);
        try {
            // NEW: Subdomain uniqueness check
            if (subdomain) {
                const q = query(collection(db, 'users'), where('plannerWebsite.subdomain', '==', subdomain));
                const snapshot = await getDocs(q);
                const isDuplicate = snapshot.docs.some(docSnap => docSnap.id !== user.uid);
                
                if (isDuplicate) {
                    toast.error('This custom URL is already taken by another user. Please choose a different one.');
                    setSaving(false);
                    return;
                }
            }

            await updateDoc(doc(db, "users", user.uid), {
                plannerWebsite: { sections, theme, isPublished: publish, updatedAt: serverTimestamp(), subdomain }
            });
            setIsPublished(publish);
            toast.success(publish ? "Website published successfully!" : "Draft saved successfully!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save website.");
        } finally {
            setSaving(false);
        }
    };

    const handleFieldChange = (sectionId, field, value) => {
        const newSections = sections.map(sec => sec.id === sectionId ? { ...sec, content: { ...sec.content, [field]: value } } : sec);
        updateDesign(newSections, theme);
    };

    const handleThemeChange = (field, value) => {
        updateDesign(sections, { ...theme, [field]: value });
    };

    // NEW: Apply Full Template
    const handleTemplateClick = (template) => {
        setTemplateToApply(template);
    };

    const confirmApplyTemplate = () => {
        if (!templateToApply) return;
        // Deep clone to prevent reference issues
        const clonedSections = JSON.parse(JSON.stringify(templateToApply.sections));
        updateDesign(clonedSections, templateToApply.theme);
        setActiveSectionId(clonedSections[0].id);
        setTemplateToApply(null);
    };

    const toggleSection = (id) => {
        const newSections = sections.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s);
        updateDesign(newSections, theme);
    };

    // NEW: Robust Section Management Tools
    const duplicateSection = (id) => {
        const secToDup = sections.find(s => s.id === id);
        if (!secToDup) return;
        const newSec = JSON.parse(JSON.stringify(secToDup));
        newSec.id = `${newSec.type}_${Date.now()}`;
        const idx = sections.findIndex(s => s.id === id);
        const newSections = [...sections];
        newSections.splice(idx + 1, 0, newSec);
        updateDesign(newSections, theme);
        setActiveSectionId(newSec.id);
    };

    const deleteSection = (id) => {
        if (sections.length <= 1) {
            toast.error("You must have at least one section.");
            return;
        }
        if (window.confirm("Are you sure you want to delete this block?")) {
            const newSections = sections.filter(s => s.id !== id);
            updateDesign(newSections, theme);
            if (activeSectionId === id) setActiveSectionId(newSections[0].id);
        }
    };

    const addNewSection = (type) => {
        let content = {};
        switch(type) {
            case 'hero': content = { title: "New Hero", subtitle: "A catchy new subtitle.", buttonText: "Click Me" }; break;
            case 'about': content = { heading: "About Us", text: "Write your story here...", layout: "left" }; break;
            case 'services': content = { heading: "Our Services", packages: [{ title: "Basic Package", price: "500", description: "Package details", features: [] }] }; break;
            case 'gallery': content = { heading: "Gallery", images: [] }; break;
            case 'contact': content = { heading: "Contact Us", text: "Get in touch for your next event." }; break;
        }
        const newSec = { id: `${type}_${Date.now()}`, type, enabled: true, content };
        updateDesign([...sections, newSec], theme);
        setActiveSectionId(newSec.id);
        setShowAddMenu(false);
    };

    const moveSection = (index, direction) => {
        const newSections = [...sections];
        if (direction === 'up' && index > 0) {
            [newSections[index], newSections[index - 1]] = [newSections[index - 1], newSections[index]];
        } else if (direction === 'down' && index < newSections.length - 1) {
            [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
        }
        updateDesign(newSections, theme);
    };

    const handleCopyUrl = () => {
        const textArea = document.createElement("textarea");
        textArea.value = publicUrl;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 3000);
        } catch (err) {
            console.error('Unable to copy', err);
        }
        document.body.removeChild(textArea);
    };

    const activeSection = sections.find(s => s.id === activeSectionId);

    const renderCommonStylesEditor = () => (
        <div className="border-t border-gray-200 pt-5 mt-5 space-y-4">
            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Section Styling</h5>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Background Color</label>
                    <div className="flex items-center gap-2">
                        <input type="color" value={activeSection.content.bgColor || '#ffffff'} onChange={e => handleFieldChange(activeSection.id, 'bgColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer" />
                        <button onClick={() => handleFieldChange(activeSection.id, 'bgColor', '')} className="text-xs text-gray-500 hover:text-red-500 transition">Clear</button>
                    </div>
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Text Color</label>
                    <div className="flex items-center gap-2">
                        <input type="color" value={activeSection.content.textColor || '#000000'} onChange={e => handleFieldChange(activeSection.id, 'textColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer" />
                        <button onClick={() => handleFieldChange(activeSection.id, 'textColor', '')} className="text-xs text-gray-500 hover:text-red-500 transition">Clear</button>
                    </div>
                </div>
            </div>
            <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Padding (Spacing)</label>
                <select value={activeSection.content.padding || '4rem 1rem'} onChange={e => handleFieldChange(activeSection.id, 'padding', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white">
                    <option value="2rem 1rem">Small (Compact)</option>
                    <option value="4rem 1rem">Medium (Standard)</option>
                    <option value="8rem 1rem">Large (Spacious)</option>
                </select>
            </div>
        </div>
    );

    const renderEditorInputs = () => {
        if (!activeSection) return null;

        return (
            <div className="space-y-5 p-5 animate-fade-in">
                <div className="flex justify-between items-center border-b border-gray-200 pb-3">
                    <h4 className="font-bold text-lg text-gray-900 capitalize">{activeSection.type} Content</h4>
                    <label className="flex items-center text-sm gap-2 cursor-pointer bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition">
                        <input type="checkbox" checked={activeSection.enabled} onChange={() => toggleSection(activeSection.id)} className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500" />
                        <span className="font-semibold text-gray-700">Visible</span>
                    </label>
                </div>

                {activeSection.type === 'hero' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Main Title</label><input type="text" value={activeSection.content.title} onChange={e => handleFieldChange(activeSection.id, 'title', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Subtitle</label><input type="text" value={activeSection.content.subtitle} onChange={e => handleFieldChange(activeSection.id, 'subtitle', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Image URL</label><input type="text" value={activeSection.content.imageUrl} onChange={e => handleFieldChange(activeSection.id, 'imageUrl', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div className="flex gap-4">
                            <div className="flex-1"><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Button Text</label><input type="text" value={activeSection.content.buttonText} onChange={e => handleFieldChange(activeSection.id, 'buttonText', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                            <div className="flex-1"><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Overlay Darken</label><input type="range" min="0" max="1" step="0.1" value={activeSection.content.overlayOpacity} onChange={e => handleFieldChange(activeSection.id, 'overlayOpacity', parseFloat(e.target.value))} className="w-full mt-4 cursor-pointer" /></div>
                        </div>
                        <div className="border-t border-gray-100 pt-4 mt-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Text Position</label>
                            <div className="bg-gray-50 p-2.5 rounded-xl inline-block border border-gray-200 shadow-inner">
                                <div className="grid grid-cols-3 gap-1">
                                    {['top-left', 'top-center', 'top-right', 'center-left', 'center-center', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'].map(pos => {
                                        const [v, h] = pos.split('-');
                                        const isActive = (activeSection.content.verticalAlign || 'center') === v && (activeSection.content.horizontalAlign || 'center') === h;
                                        return (
                                            <button
                                                key={pos}
                                                onClick={() => {
                                                    handleFieldChange(activeSection.id, 'verticalAlign', v);
                                                    handleFieldChange(activeSection.id, 'horizontalAlign', h);
                                                }}
                                                className={`w-10 h-10 rounded-md border flex items-center justify-center transition-all ${isActive ? 'bg-pink-500 border-pink-600 shadow-md transform scale-105 z-10' : 'bg-white border-gray-300 hover:bg-gray-100'}`}
                                                title={pos.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            >
                                                <div className={`w-3 h-3 rounded-sm ${isActive ? 'bg-white shadow-sm' : 'bg-gray-300'}`}></div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {activeSection.type === 'about' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Heading</label><input type="text" value={activeSection.content.heading} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Bio / Story</label><textarea value={activeSection.content.text} onChange={e => handleFieldChange(activeSection.id, 'text', e.target.value)} className="w-full p-2.5 border rounded-lg h-40 resize-none mt-1" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Photo URL</label><input type="text" value={activeSection.content.imageUrl} onChange={e => handleFieldChange(activeSection.id, 'imageUrl', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Image Position</label>
                            <select value={activeSection.content.layout} onChange={e => handleFieldChange(activeSection.id, 'layout', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1 bg-white">
                                <option value="left">Image Left</option><option value="right">Image Right</option>
                            </select>
                        </div>
                    </>
                )}

                {activeSection.type === 'services' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={activeSection.content.heading} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Subtitle</label><input type="text" value={activeSection.content.subtitle} onChange={e => handleFieldChange(activeSection.id, 'subtitle', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div className="border-t pt-4 mt-4 space-y-6">
                            <label className="text-sm font-bold text-gray-900">Manage Packages</label>
                            {activeSection.content.packages.map((pkg, idx) => (
                                <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 relative group">
                                    <button onClick={() => {
                                        const newPkgs = [...activeSection.content.packages];
                                        newPkgs.splice(idx, 1);
                                        handleFieldChange(activeSection.id, 'packages', newPkgs);
                                    }} className="absolute top-2 right-2 text-red-500 opacity-0 group-hover:opacity-100 font-bold p-1">✕</button>
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <input type="text" value={pkg.title} onChange={e => { const newPkgs = [...activeSection.content.packages]; newPkgs[idx].title = e.target.value; handleFieldChange(activeSection.id, 'packages', newPkgs); }} placeholder="Package Name" className="p-2 border rounded text-sm" />
                                        <input type="text" value={pkg.price} onChange={e => { const newPkgs = [...activeSection.content.packages]; newPkgs[idx].price = e.target.value; handleFieldChange(activeSection.id, 'packages', newPkgs); }} placeholder="Price (e.g. 1500)" className="p-2 border rounded text-sm" />
                                    </div>
                                    <textarea value={pkg.description} onChange={e => { const newPkgs = [...activeSection.content.packages]; newPkgs[idx].description = e.target.value; handleFieldChange(activeSection.id, 'packages', newPkgs); }} placeholder="Description" className="w-full p-2 border rounded text-sm h-20 resize-none" />
                                    
                                    <div className="space-y-2 mt-3 border-t border-gray-200 pt-3">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Included Features</label>
                                        {(pkg.features || []).map((feature, fIdx) => (
                                            <div key={fIdx} className="flex items-center gap-2">
                                                <input 
                                                    type="text" 
                                                    value={feature} 
                                                    onChange={e => {
                                                        const newPkgs = [...activeSection.content.packages];
                                                        newPkgs[idx].features[fIdx] = e.target.value;
                                                        handleFieldChange(activeSection.id, 'packages', newPkgs);
                                                    }} 
                                                    placeholder="e.g. Vendor Management"
                                                    className="flex-grow p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-pink-500 outline-none" 
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        const newPkgs = [...activeSection.content.packages];
                                                        newPkgs[idx].features.splice(fIdx, 1);
                                                        handleFieldChange(activeSection.id, 'packages', newPkgs);
                                                    }}
                                                    className="text-gray-400 hover:text-red-500 font-bold p-1 rounded"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                        <button 
                                            type="button"
                                            onClick={() => {
                                                const newPkgs = [...activeSection.content.packages];
                                                newPkgs[idx].features.push('');
                                                handleFieldChange(activeSection.id, 'packages', newPkgs);
                                            }}
                                            className="text-xs bg-pink-50 text-pink-600 px-3 py-1.5 rounded-lg font-bold hover:bg-pink-100 transition"
                                        >
                                            + Add Feature
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button onClick={() => handleFieldChange(activeSection.id, 'packages', [...activeSection.content.packages, { title: 'New Package', price: '', description: '', features: [] }])} className="w-full bg-white border-2 border-dashed border-gray-300 py-3 rounded-xl font-bold text-gray-500 hover:text-pink-600 hover:border-pink-300 transition">
                                + Add Package
                            </button>
                        </div>
                    </>
                )}

                {activeSection.type === 'gallery' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={activeSection.content.heading || ''} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        <div className="border-t border-gray-100 pt-4 space-y-3">
                            <label className="text-sm font-bold text-gray-900 block">Images</label>
                            {(activeSection.content.images || []).map((img, idx) => (
                                <div key={idx} className="flex gap-3 items-center bg-gray-50 p-2 rounded-xl border border-gray-200">
                                    <img src={img.url || 'https://placehold.co/40'} className="w-12 h-12 object-cover rounded-lg bg-gray-200 shadow-sm border border-gray-300" alt="thumb"/>
                                    <input type="text" value={img.url || ''} onChange={e => { const newImgs = [...activeSection.content.images]; newImgs[idx].url = e.target.value; handleFieldChange(activeSection.id, 'images', newImgs); }} placeholder="Paste Image URL" className="flex-1 p-2 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-pink-500 outline-none" />
                                    <button onClick={() => { const newImgs = [...activeSection.content.images]; newImgs.splice(idx, 1); handleFieldChange(activeSection.id, 'images', newImgs); }} className="p-2 text-gray-400 hover:text-red-500 bg-white rounded-lg shadow-sm border border-gray-100 font-bold">✕</button>
                                </div>
                            ))}
                            <button onClick={() => handleFieldChange(activeSection.id, 'images', [...(activeSection.content.images || []), { url: '' }])} className="w-full py-3 bg-white border-2 border-dashed border-gray-300 rounded-xl font-bold text-gray-500 hover:text-pink-600 hover:border-pink-300 transition">
                                + Paste Image URL
                            </button>
                            <ImageUpload 
                                weddingId={user.uid} 
                                user={user} 
                                onUploadComplete={(url) => handleFieldChange(activeSection.id, 'images', [...(activeSection.content.images || []), { url, caption: '' }])} 
                                buttonText="+ Upload & Crop Image"
                                hideLabel={true}
                            />
                        </div>
                    </>
                )}

                {activeSection.type === 'rsvp' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={activeSection.content.heading || ''} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mt-2">
                            <p className="text-sm text-blue-800 font-medium">The RSVP form is automatically connected to your dashboard.</p>
                        </div>
                    </>
                )}

                {/* Unified Section Style Editing for all sections */}
                {renderCommonStylesEditor()}
            </div>
        );
    };

    return (
        <div className="flex flex-col lg:flex-row h-full bg-white border-t border-gray-200">
            {/* NEW: Custom Template Warning Modal */}
            {templateToApply && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-[300] flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm">
                        <h3 className="text-xl font-bold mb-4 text-gray-900">Apply Template?</h3>
                        <p className="mb-6 text-gray-600 text-sm">Applying the <strong>{templateToApply.name}</strong> template will replace your current design and sections. Are you sure you want to continue?</p>
                        <div className="flex justify-end gap-3 pt-4 border-t">
                            <button onClick={() => setTemplateToApply(null)} className="px-5 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                            <button onClick={confirmApplyTemplate} className="px-5 py-2 bg-pink-600 text-white rounded-lg font-bold hover:bg-pink-700 transition shadow-md">Apply Template</button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Editor Sidebar */}
            <div className="w-full lg:w-[400px] flex flex-col border-r border-gray-200 bg-gray-50 z-10 shadow-[2px_0_10px_rgba(0,0,0,0.05)] flex-shrink-0">
                <div className="p-5 border-b border-gray-200 bg-white flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-extrabold text-gray-900">Website Builder</h2>
                        <p className="text-sm text-gray-500 mt-1">Design your public planner portfolio.</p>
                    </div>
                    {/* NEW: Dedicated Undo/Redo Buttons */}
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-lg border border-gray-200 shadow-sm">
                        <button onClick={handleUndo} disabled={past.length === 0} className="p-1.5 rounded-md hover:bg-white text-gray-600 disabled:opacity-30 transition" title="Undo">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                        </button>
                        <div className="w-px bg-gray-300 mx-1 my-1"></div>
                        <button onClick={handleRedo} disabled={future.length === 0} className="p-1.5 rounded-md hover:bg-white text-gray-600 disabled:opacity-30 transition" title="Redo">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>
                        </button>
                    </div>
                </div>

                {/* NEW: Editor Tabs */}
                <div className="flex border-b border-gray-200 bg-gray-100 flex-shrink-0">
                    <button 
                        onClick={() => setEditorTab('content')} 
                        className={`flex-1 py-2.5 text-sm font-bold border-b-2 transition-colors ${editorTab === 'content' ? 'border-pink-500 text-pink-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                    >
                        Content & Blocks
                    </button>
                    <button 
                        onClick={() => setEditorTab('design')} 
                        className={`flex-1 py-2.5 text-sm font-bold border-b-2 transition-colors ${editorTab === 'design' ? 'border-pink-500 text-pink-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                    >
                        Theme Design
                    </button>
                </div>

                {/* NEW: Subdomain Custom URL Input */}
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2 block">Your Custom URL</label>
                    <div className="flex items-center bg-white border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-pink-500 transition shadow-sm">
                        <span className="text-gray-400 text-sm pl-3 font-mono">/planner/</span>
                        <input
                            type="text"
                            value={subdomain}
                            onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            className="font-mono p-2 text-sm bg-transparent border-none outline-none w-full text-gray-900 font-bold"
                            placeholder="your-name"
                        />
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
                    {/* Global Theme Settings - Only visible in 'design' tab */}
                    {editorTab === 'design' && (
                        <div className="p-5 bg-white space-y-4 animate-fade-in">
                            
                            {/* NEW: Pre-built Templates */}
                            <div className="mb-6 border-b border-gray-100 pb-6">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-3">Website Templates</label>
                                <div className="grid grid-cols-1 gap-3">
                                    {portfolioTemplates.map(tpl => (
                                        <div 
                                            key={tpl.id} 
                                            onClick={() => handleTemplateClick(tpl)}
                                            className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-pink-300 hover:shadow-md transition group flex items-center justify-between bg-gray-50 hover:bg-white"
                                        >
                                            <div>
                                                <p className="font-bold text-gray-900 group-hover:text-pink-600 transition" style={{ fontFamily: tpl.theme.fontFamily }}>{tpl.name}</p>
                                                <div className="flex gap-1 mt-1.5">
                                                    <span className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: tpl.theme.primaryColor }}></span>
                                                    <span className="w-3 h-3 rounded-full shadow-sm border border-gray-200" style={{ backgroundColor: tpl.theme.backgroundColor }}></span>
                                                </div>
                                            </div>
                                            <span className="text-xs font-semibold text-gray-500 bg-white border border-gray-200 px-2 py-1 rounded shadow-sm group-hover:text-pink-600">Apply</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* NEW: Preset Palettes */}
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-3">Designer Palettes</label>
                                <div className="flex flex-wrap gap-2">
                                    {colorPalettes.map(palette => {
                                        const isActive = theme.primaryColor === palette.primary && theme.backgroundColor === palette.bg;
                                        return (
                                            <button
                                                key={palette.name}
                                                onClick={() => updateDesign(sections, { ...theme, primaryColor: palette.primary, backgroundColor: palette.bg })}
                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all duration-300 ${isActive ? 'border-gray-900 bg-gray-900 shadow-md ring-2 ring-gray-900/20 transform scale-[1.02]' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm'}`}
                                                title={palette.name}
                                            >
                                                <div className="flex -space-x-1.5 drop-shadow-sm">
                                                    <div className="w-4 h-4 rounded-full border border-white/50 z-10" style={{ backgroundColor: palette.primary }}></div>
                                                    <div className={`w-4 h-4 rounded-full border z-0 ${isActive ? 'border-white/20' : 'border-black/10'}`} style={{ backgroundColor: palette.bg }}></div>
                                                </div>
                                                <span className={`text-[11px] font-bold tracking-wide ${isActive ? 'text-white' : 'text-gray-600'}`}>{palette.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-100">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Theme Color</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={theme.primaryColor} onChange={e => handleThemeChange('primaryColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer" />
                                        <input type="text" value={theme.primaryColor} onChange={e => handleThemeChange('primaryColor', e.target.value)} className="flex-1 p-1.5 border border-gray-300 rounded-md text-xs font-mono font-bold" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Background</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={theme.backgroundColor} onChange={e => handleThemeChange('backgroundColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer" />
                                        <input type="text" value={theme.backgroundColor} onChange={e => handleThemeChange('backgroundColor', e.target.value)} className="flex-1 p-1.5 border border-gray-300 rounded-md text-xs font-mono font-bold" />
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Typography</label>
                                    <select value={theme.fontFamily} onChange={e => handleThemeChange('fontFamily', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-xs bg-white">
                                        <option value="'Playfair Display', serif">Playfair Display</option>
                                        <option value="'Inter', sans-serif">Inter</option>
                                        <option value="'Lato', sans-serif">Lato</option>
                                        <option value="'Georgia', serif">Georgia</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Corner Radius</label>
                                    <select value={theme.borderRadius} onChange={e => handleThemeChange('borderRadius', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-xs bg-white">
                                        <option value="0px">Sharp (0px)</option>
                                        <option value="0.5rem">Rounded (8px)</option>
                                        <option value="1rem">Extra Rounded (16px)</option>
                                        <option value="9999px">Pill (Full)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content Editor - Only visible in 'content' tab */}
                    {editorTab === 'content' && (
                        <div className="flex-1 flex flex-col animate-fade-in">
                            {/* UPDATED: Robust Section List */}
                            <div className="p-3 border-b border-gray-200 bg-gray-100 flex-shrink-0 z-20">
                                <div className="flex justify-between items-center mb-3 ml-2 pr-1">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Page Builder</p>
                                    <div className="relative">
                                        <button onClick={() => setShowAddMenu(!showAddMenu)} className="text-xs bg-white border border-gray-200 px-3 py-1.5 rounded shadow-sm hover:bg-gray-50 font-bold text-gray-800 flex items-center gap-1.5 transition">
                                            <span className="text-lg leading-none mb-0.5">+</span> Add Block
                                        </button>
                                        {showAddMenu && (
                                            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 shadow-2xl rounded-lg overflow-hidden animate-fade-in">
                                                <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-[10px] font-bold text-gray-500 uppercase">Select Block Type</div>
                                                {['hero', 'about', 'services', 'gallery', 'contact'].map(t => (
                                                    <button key={t} onClick={() => addNewSection(t)} className="w-full text-left px-4 py-2.5 text-sm font-medium hover:bg-pink-50 hover:text-pink-600 capitalize border-b border-gray-50 last:border-0 transition">{t}</button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-2 max-h-[30vh] overflow-y-auto custom-scrollbar pr-1">
                                    {sections.map((section, index) => (
                                        <div 
                                            key={section.id} 
                                            onClick={() => setActiveSectionId(section.id)}
                                            className={`p-3 rounded-lg cursor-pointer border flex flex-col gap-3 transition-all ${activeSectionId === section.id ? 'bg-white border-pink-500 shadow-md ring-1 ring-pink-500/20' : 'bg-gray-50 border-gray-200 hover:bg-white hover:border-gray-300 hover:shadow-sm'}`}
                                        >
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-4 h-4 flex items-center justify-center cursor-grab text-gray-400 opacity-50 hover:opacity-100">⋮⋮</div>
                                                    <span className={`font-bold text-sm uppercase tracking-wide ${!section.enabled ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{section.type}</span>
                                                </div>
                                                <div className="flex gap-1 bg-gray-100 rounded-md p-0.5 border border-gray-200" onClick={e => e.stopPropagation()}>
                                                    <button onClick={() => moveSection(index, 'up')} disabled={index===0} className="w-6 h-6 flex items-center justify-center hover:bg-white rounded text-gray-600 font-bold disabled:opacity-30">↑</button>
                                                    <button onClick={() => moveSection(index, 'down')} disabled={index===sections.length-1} className="w-6 h-6 flex items-center justify-center hover:bg-white rounded text-gray-600 font-bold disabled:opacity-30">↓</button>
                                                </div>
                                            </div>
                                            
                                            {/* Action row (duplicate/delete) only shows for active section */}
                                            {activeSectionId === section.id && (
                                                <div className="flex justify-between items-center pt-3 border-t border-pink-100 mt-1 animate-fade-in" onClick={e => e.stopPropagation()}>
                                                    <label className="flex items-center text-xs gap-1.5 cursor-pointer text-gray-600 font-medium hover:text-gray-900">
                                                        <input type="checkbox" checked={section.enabled} onChange={() => toggleSection(section.id)} className="w-3.5 h-3.5 text-pink-600 rounded focus:ring-pink-500 border-gray-300" />
                                                        Visible
                                                    </label>
                                                    <div className="flex gap-3">
                                                        <button onClick={() => duplicateSection(section.id)} className="text-gray-500 hover:text-blue-600 text-xs font-bold transition flex items-center gap-1">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> Copy
                                                        </button>
                                                        <button onClick={() => deleteSection(section.id)} className="text-gray-500 hover:text-red-600 text-xs font-bold transition flex items-center gap-1">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg> Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Active Section Editor */}
                            <div className="flex-1 bg-white">
                                {renderEditorInputs()}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-5 border-t border-gray-200 bg-white space-y-3 shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                    <div className="flex gap-2">
                        <button onClick={() => handleSave(false)} disabled={saving || isAutoSaving} className="flex-1 bg-white border border-gray-300 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-50 transition shadow-sm disabled:opacity-50">
                            {isAutoSaving ? 'Auto-saving...' : 'Save Draft'}
                        </button>
                        <button onClick={() => handleSave(true)} disabled={saving || isAutoSaving} className="flex-1 bg-gray-900 text-white py-3 rounded-xl font-bold hover:bg-black transition shadow-md disabled:opacity-50 flex items-center justify-center gap-2">
                            {(saving || isAutoSaving) ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> : 'Publish'}
                        </button>
                    </div>

                    {isPublished && (
                        <button onClick={() => window.open(publicUrl, '_blank')} className="w-full py-2.5 rounded-lg font-bold text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 transition flex items-center justify-center gap-2">
                            <span>🌐</span> View Live Website
                        </button>
                    )}
                    
                    <div className="pt-2">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center mb-2">Your Public URL</p>
                        <button onClick={handleCopyUrl} className={`w-full py-2.5 rounded-lg font-bold text-sm border-2 transition flex items-center justify-center gap-2 ${copiedLink ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                            {copiedLink ? '✅ Link Copied!' : <><PlannerIcons.Share /> Copy Link to Share</>}
                        </button>
                    </div>
                </div>
            </div>

            {/* UPDATED: Live Preview Panel with Responsive Toggles */}
            <div className="flex-1 bg-gray-200 overflow-hidden relative flex flex-col">
                {/* Viewport Toggles */}
                <div className="bg-gray-100 border-b border-gray-300 p-2.5 flex justify-center items-center gap-4 shadow-inner z-10 flex-shrink-0">
                    <div className="bg-white p-1 rounded-lg flex border border-gray-200 shadow-sm">
                        <button 
                            onClick={() => setPreviewDevice('desktop')} 
                            className={`px-4 py-1.5 rounded-md text-sm font-bold flex items-center gap-2 transition ${previewDevice === 'desktop' ? 'bg-gray-900 text-white shadow' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            Desktop
                        </button>
                        <button 
                            onClick={() => setPreviewDevice('mobile')} 
                            className={`px-4 py-1.5 rounded-md text-sm font-bold flex items-center gap-2 transition ${previewDevice === 'mobile' ? 'bg-gray-900 text-white shadow' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            Mobile
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-8 flex justify-center items-start">
                    {/* Wrap renderer in a container to simulate a browser window or mobile phone */}
                    <div className={`bg-white shadow-2xl relative transition-all duration-500 flex flex-col flex-shrink-0 ${previewDevice === 'mobile' ? 'w-[375px] rounded-[2rem] border-[12px] border-gray-900 overflow-hidden h-[812px]' : 'w-full max-w-[1400px] min-h-full'}`}>
                        {/* Mobile Notch simulation */}
                        {previewDevice === 'mobile' && (
                            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-xl z-[60]"></div>
                        )}
                        
                        <div className="flex-1 overflow-hidden relative rounded-[1rem] md:rounded-none">
                            {previewDevice === 'mobile' ? (
                                <IFramePreview className="w-full h-full bg-white border-none z-10 relative">
                                    <PlannerWebsiteRenderer sections={sections} theme={theme} planner={user} onInquire={() => {}} />
                                </IFramePreview>
                            ) : (
                                <div className="w-full h-full overflow-y-auto custom-scrollbar relative">
                                    <PlannerWebsiteRenderer sections={sections} theme={theme} planner={user} onInquire={() => {}} />
                                    
                                    {/* Visual Overlay for Active Section */}
                                    <div className="absolute inset-0 pointer-events-none z-50 flex flex-col justify-start">
                                        {sections.map(section => (
                                            <div key={section.id} className={`w-full ${activeSectionId === section.id && section.enabled ? 'ring-inset ring-4 ring-pink-500/30 bg-pink-500/5' : ''}`}></div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}